import type { TFunction } from "i18next";
import { toast } from "@/hooks/use-toast";
import { isRateLimitError } from "@/lib/api-error";

/**
 * Surface the shared rate-limit (HTTP 429) error as a friendly,
 * seconds-aware "please wait" toast.
 *
 * `bookings-api` and `users-api` throw a shared `RateLimitError` on a
 * 429, carrying a pre-formatted, seconds-aware message (e.g. "Too many
 * attempts. Please wait ~30 seconds and try again."). Imperative call
 * sites (mutations / button handlers) catch these errors and otherwise
 * fall back to a generic "something went wrong" toast — or, worse, drop
 * the message entirely (title-only toasts) — which hides the wait time
 * from a hammered user.
 *
 * Use this as the first line of a `catch` block:
 *
 *   } catch (err) {
 *     if (showRateLimitToastIfNeeded(err, t)) return;
 *     // …existing generic handling…
 *   }
 *
 * It shows the toast and returns `true` only for a `RateLimitError`,
 * leaving every other error for the caller's existing handling
 * (returns `false`). The toast uses the default (neutral) variant — a
 * "please wait" is informational, not a hard failure — and the error's
 * own message as the description; the localized fallback only applies if
 * that message is empty. `RateLimitError` is already suppressed in
 * `lib/sentry.ts`, so this adds no Sentry noise.
 */
export function showRateLimitToastIfNeeded(error: unknown, t: TFunction): boolean {
  if (!isRateLimitError(error)) return false;
  toast({
    title: t("common.rate_limited_title", "Please wait"),
    description:
      error.message ||
      t("common.rate_limited_desc", "Too many attempts. Please wait a moment and try again."),
  });
  return true;
}
